import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Banner, Button, DataTable, Dialog, StatusChip, TextField, useSnackbar } from '@meshble/molo';
import { api, must, type Instance } from '../lib/api';
import { useIstanze } from '../lib/dati';
import { restaAllaCopia } from '../lib/copie';
import { ETICHETTA, TONO_ISTANZA } from './instances';
import { Anello } from '../brand';
import { t } from '../lib/lingua';
import { ConPermesso } from '../permesso';
import { usePermessiSu } from '../session';

/**
 * Le copie di prova.
 *
 * Una copia deve sembrare una copia prima di tutto il resto: ha i dati veri
 * di un cliente, e davanti a una scheda uguale a quella della produzione si
 * finisce a provare sulla produzione. Per questo ha un colore suo, il
 * `tertiary`, uguale in lista, nel server e nella sua scheda.
 */

/** Il segno di una copia, lo stesso ovunque compaia. */
export function ChipCopia({ staging }: { staging: NonNullable<Instance['staging']> }) {
  return <StatusChip status="copia" label={t('Copia di prova · {resta}', { resta: restaAllaCopia(staging.days) })} tone="tertiary" />;
}

/**
 * In cima alla scheda di una copia: di chi è, quanto le resta, e come si
 * ricarica. Sta sopra la salute perché è la prima cosa da sapere, e senza
 * scorrere.
 */
export function FasciaCopia({ slug, istanza }: { slug: string; istanza: Instance }) {
  const permessi = usePermessiSu(slug, { istanza: istanza.id });
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const navigate = useNavigate();
  const ricarica = useMutation({
    mutationFn: () =>
      must(
        api.POST('/api/v1/orgs/{slug}/instances/{id}/staging/refresh', {
          params: { path: { slug, id: istanza.id } },
        }),
      ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['instance', slug, istanza.id] });
      avvisa(t('Ricarico la copia dai dati di adesso'));
      if (r.jobId) navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: r.jobId } });
    },
  });

  const st = istanza.staging;
  if (!st) return null;

  if (st.days < 0) {
    return (
      <Banner tone="error" title={t('Copia di prova scaduta')}>
        {t('Viene cancellata al prossimo giro, che passa ogni ora.')}
      </Banner>
    );
  }

  const quando = st.days === 0 ? t('Oggi è il suo ultimo giorno') : t('Sparisce fra {resta}', { resta: restaAllaCopia(st.days) });
  return (
    <>
      <Banner
        tone="tertiary"
        title={st.fromId
          ? t('Copia di prova di {origine}', { origine: <Link to="/orgs/$slug/instances/$id" params={{ slug, id: st.fromId }}>{st.from}</Link> })
          : t('Copia di prova')}
        action={st.fromId && (
          <ConPermesso ha={permessi} serve="staging.refresh">
            <Button
              variant="outlined"
              icon={ricarica.isPending ? <Anello /> : undefined}
              disabled={istanza.status !== 'running' || ricarica.isPending}
              onClick={() => ricarica.mutate()}
            >
              {t('Aggiorna dalla produzione')}
            </Button>
          </ConPermesso>
        )}
      >
        {t('Dati veri di un cliente, e niente esce: cron spenti, posta disattivata, codice Enterprise tolto.')}{' '}
        {st.days <= 7 ? t('{quando}: se ti serve ancora, rifalla.', { quando }) : `${quando}.`}{' '}
        {st.fromId
          ? t('«Aggiorna dalla produzione» riporta dati e codice a quelli di adesso; un repository spostato su un altro ramo resta sul suo.')
          : t('L’istanza da cui veniva non esiste più, quindi non c’è più niente da cui ricaricarla.')}
      </Banner>
      {ricarica.error && (
        <Banner tone="error" title={t('Non l’ho ricaricata')}>{(ricarica.error as Error).message}</Banner>
      )}
    </>
  );
}

/**
 * Nella scheda di una produzione: le copie che ha, e il modo di farne una.
 * Prima offriva solo di crearla, e da *acme* non si vedeva che *acme-staging*
 * c'era già.
 */
export function Staging({ slug, istanza }: { slug: string; istanza: Instance }) {
  const permessi = usePermessiSu(slug, { istanza: istanza.id });
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const navigate = useNavigate();
  const [apri, setApri] = useState(false);
  const [nome, setNome] = useState('');
  const istanze = useIstanze(slug);
  const copie = (istanze.data?.instances ?? []).filter((i) => i.staging?.fromId === istanza.id);

  const crea = useMutation({
    mutationFn: () =>
      must(
        api.POST('/api/v1/orgs/{slug}/instances/{id}/staging', {
          params: { path: { slug, id: istanza.id } },
          body: nome.trim() ? { name: nome.trim() } : {},
        }),
      ),
    onSuccess: (i) => {
      qc.invalidateQueries({ queryKey: ['instances', slug] });
      setApri(false);
      setNome('');
      avvisa(t('Sto copiando {da} in {a}', { da: istanza.name, a: i.name }));
      if (i.jobId) navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: i.jobId } });
    },
  });

  return (
    <div className="md-stack">
      <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
        {t('Una copia di questa istanza sullo stesso server, con i suoi dati e il suo codice, su cui provare un aggiornamento, un modulo nuovo o un altro ramo. La produzione non si ferma.')}
      </p>
      {copie.length > 0 && (
        <DataTable
          columns={[
            { key: 'nome', header: t('Copia') },
            { key: 'stato', header: t('Stato') },
            { key: 'resta', header: t('Sparisce fra'), nowrap: true },
          ]}
          rowKey={(r) => String(r.id)}
          onRowClick={(r) => navigate({ to: '/orgs/$slug/instances/$id', params: { slug, id: String(r.id) } })}
          rows={copie.map((c) => ({
            id: c.id,
            nome: c.name,
            stato: <StatusChip status={c.status} label={t(ETICHETTA[c.status] ?? c.status)} tone={TONO_ISTANZA[c.status]} />,
            resta: restaAllaCopia(c.staging!.days),
          }))}
        />
      )}
      {crea.error && <Banner tone="error" title={t('Non l’ho creata')}>{(crea.error as Error).message}</Banner>}
      <div className="md-stack__inline">
        <ConPermesso ha={permessi} serve="staging.create">
          <Button variant="tonal" disabled={istanza.status !== 'running'} onClick={() => setApri(true)}>
            {copie.length > 0 ? t('Crea un’altra copia') : t('Crea una copia di prova')}
          </Button>
        </ConPermesso>
      </div>

      <Dialog
        open={apri}
        onOpenChange={(v) => !v && setApri(false)}
        title={t('Crea una copia di prova')}
        description={t('Stessi dati, stesso codice, stesso server, e niente che esca verso il mondo.')}
        actions={
          <>
            <Button variant="text" onClick={() => setApri(false)}>{t('Annulla')}</Button>
            <Button
              variant="filled"
              icon={crea.isPending ? <Anello /> : undefined}
              disabled={crea.isPending}
              onClick={() => crea.mutate()}
            >
              {crea.isPending ? t('Copio…') : t('Crea')}
            </Button>
          </>
        }
      >
        <div className="md-stack">
          <p className="md-body-medium" style={{ margin: 0 }}>
            {t('La copia nasce {neutralizzata}: cron spenti, server di posta disattivati, codice Enterprise tolto. Senza, al primo giro di cron manderebbe fatture vere a clienti veri.', {
              neutralizzata: <b>{t('neutralizzata')}</b>,
            })}
          </p>
          <p className="md-body-medium" style={{ margin: 0 }}>
            {t('{forte}: Odoo, Enterprise e i repository di moduli allo stesso commit della produzione. Il ramo di un repository si cambia poi sulla sola copia.', {
              forte: <b>{t('Con lo stesso codice')}</b>,
            })}
          </p>
          <p className="md-body-medium" style={{ margin: 0 }}>
            {t('{forte}, poi viene cancellata. Ha dentro i dati veri di un cliente, e una copia dimenticata è una superficie in più.', {
              forte: <b>{t('Dura trenta giorni')}</b>,
            })}
          </p>
          <TextField
            label={t('Nome')}
            value={nome}
            onChange={(e) => setNome(e.target.value.toLowerCase())}
            placeholder={`${istanza.name}-prova`}
            help={t('Vuoto: «nome-prova». Nasce con il suo indirizzo, come ogni istanza.')}
          />
        </div>
      </Dialog>
    </div>
  );
}
